import React, { useMemo } from 'react'
import type { ComponentConfig } from '@puckeditor/core'
import { useTranslation } from 'i18n'
import { WithLayout, withLayout } from '../Layout'
import PrivacyRightsRequestForm from 'components/PrivacyRightsRequestForm/PrivacyRightsRequestForm'
import DataDownloadForm from 'components/DataDownloadForm/DataDownloadForm'
import privacyRequestFormJson from 'components/PrivacyRightsRequestForm/privacyRequestForm.json'
import dataDownloadFormJson from 'components/DataDownloadForm/dataDownloadForm.json'
import { FORM_LABEL_KEYS, DOWNLOAD_LABEL_KEYS } from './formLabelKeys'
import * as styles from './PrivacyRightsRequest.module.scss'

/**
 * The whole "Privacy Rights Request Form and Request to Not Sell or Share Personal Data"
 * page, as one Puck block (BR-5590).
 *
 * It exists as a block rather than a page template because Celebrando serves one page per
 * language: an author drops this on the English page and on the Spanish one, and each
 * renders in its own language. A page template would have to be duplicated per site, and
 * the copy would drift.
 *
 * The copy is legal text, so it is NOT author-editable here. It lives in the `privacyRights`
 * catalog, keyed per paragraph rather than per sentence, so a translator or the legal team
 * reviews a whole clause at a time. Only the Data Privacy Officer's contact details are
 * fields, because those change without a legal review.
 *
 * The form itself is the same component and the same endpoint dailybreak uses. Only the
 * labels are translated — the field `name`s are what the API reads and are untouched, so a
 * Spanish submission arrives in exactly the shape the existing handler expects. The service
 * already stamps the originating site onto `privacyRightsRequest`, which is how Celebrando
 * submissions are told apart without a new inbox.
 */
export type PrivacyRightsRequestProps = WithLayout<{
  dpoName?: string
  dpoEmail?: string
  dpoPhone?: string
  dpoAddress?: string
  lastUpdated?: string
}>

const RIGHT_KEYS = [
  'informedProcessing',
  'informedSharing',
  'access',
  'rectify',
  'erasure',
  'limit',
  'optOut',
  'withdraw',
  'portability',
  'nonDiscrimination',
] as const

const PrivacyRightsRequestRender = ({
  dpoName,
  dpoEmail,
  dpoPhone,
  dpoAddress,
  lastUpdated,
}: PrivacyRightsRequestProps) => {
  const { t } = useTranslation('privacyRights')

  // A field with no key in the map keeps its English label rather than rendering a raw
  // key, so adding a field to the JSON degrades instead of breaking.
  const fields = useMemo(
    () =>
      privacyRequestFormJson.fields.map(field => {
        const key = FORM_LABEL_KEYS[field.name as string]
        return key ? { ...field, label: t(key) } : field
      }),
    [t],
  )

  const success = useMemo(
    () => ({ title: t('success.title'), description: t('success.description') }),
    [t],
  )

  // The download form is the self-service route for the access and portability rights the
  // list above promises, and it works from an email address rather than a login — the
  // token source it reuses is anonymous-allowed.
  const downloadFields = useMemo(
    () =>
      dataDownloadFormJson.fields.map(field => {
        const key = DOWNLOAD_LABEL_KEYS[field.name as string]
        return key ? { ...field, label: t(key) } : field
      }),
    [t],
  )

  const downloadSuccess = useMemo(
    () => ({
      title: t('download.successTitle'),
      description: t('download.successDescription'),
    }),
    [t],
  )

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>{t('title')}</h1>
      <p>{t('intro')}</p>
      <ul className={styles.rights}>
        {RIGHT_KEYS.map(key => (
          <li key={key}>{t(`rights.${key}`)}</li>
        ))}
      </ul>
      <p>{t('instructions')}</p>

      <PrivacyRightsRequestForm
        fields={fields}
        submitLabel={t('form.submit')}
        success={success}
      />

      <h2 className={styles.downloadHeading}>{t('download.heading')}</h2>
      <p>{t('download.body')}</p>
      <DataDownloadForm
        fields={downloadFields}
        submitLabel={t('download.submit')}
        success={downloadSuccess}
      />

      <p className={styles.disclaimer}>{t('disclaimer')}</p>

      <p>{t('contact.intro')}</p>
      <div className={styles.contact}>
        <p>
          <strong>{t('contact.name')}</strong>
          <br />
          {dpoName}
        </p>
        <p>
          <strong>{t('contact.email')}</strong>
          <br />
          <a href={`mailto:${dpoEmail}`}>{dpoEmail}</a>
        </p>
        <p>
          <strong>{t('contact.address')}</strong>
          <br />
          {/* Authored as one field with line breaks, so legal can change the address
              without anyone touching the markup. */}
          {String(dpoAddress ?? '')
            .split('\n')
            .map((line, i) => (
              <React.Fragment key={i}>
                {line}
                <br />
              </React.Fragment>
            ))}
        </p>
        <p>
          <strong>{t('contact.phone')}</strong>
          <br />
          {dpoPhone}
        </p>
        {lastUpdated ? <p>{t('contact.lastUpdated', { date: lastUpdated })}</p> : null}
      </div>
    </div>
  )
}

const PrivacyRightsRequestInternal: ComponentConfig<PrivacyRightsRequestProps> = {
  label: 'Privacy Rights Request',
  fields: {
    dpoName: { type: 'text', label: 'Data Privacy Officer — Name' },
    dpoEmail: { type: 'text', label: 'Data Privacy Officer — Email' },
    dpoPhone: { type: 'text', label: 'Data Privacy Officer — Phone' },
    dpoAddress: {
      type: 'textarea',
      label: 'Data Privacy Officer — Address (one line per row)',
    },
    lastUpdated: {
      type: 'text',
      label: 'Last Updated (set per page: 08/31/2026 in English, 31/08/2026 in Spanish)',
      placeholder: '08/31/2026',
    },
  },
  defaultProps: {
    dpoName: 'Josh Ginsberg',
    dpoEmail: 'privacy@dailybreak.com',
    dpoPhone: '(617)-544-9078',
    dpoAddress: 'Breaktime Media\n24 School St 2nd floor\nBoston, MA 02108',
    lastUpdated: '08/31/2026',
  },
  render: PrivacyRightsRequestRender,
}

export const PuckPrivacyRightsRequest = withLayout(PrivacyRightsRequestInternal)
